
import React, { useEffect, useRef, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  IconButton,
  InputAdornment,
  TextField,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

dayjs.extend(utc);
const shortcutsItems = [
  {
    label: "Last 7 Days",
    getValue: () => {
      const today = dayjs();
      return [today.subtract(7, "day").toDate(), today.toDate()];
    },
  },
  {
    label: "Current Month",
    getValue: () => {
      const today = dayjs();
      return [today.startOf("month").toDate(), today.toDate()];
    },
  },
  {
    label: "Last Month",
    getValue: () => {
      const today = dayjs();
      const prevMonth = today.subtract(1, "month");
      return [
        prevMonth.startOf("month").toDate(),
        prevMonth.endOf("month").toDate(),
      ];
    },
  },
];

const DateRangePickerWithFilter = ({
  startDateValue,
  endDateValue,
  setStartDateValue,
  setEndDateValue,
  setIsRefereshData,
  loading,
}) => {
  const [selectedDates, setSelectedDates] = useState([new Date(), new Date()]);
  const [isOpen, setIsOpen] = useState(false);
  const calendarRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [calendarRef]);

  useEffect(() => {
    const hideLicenseKeyMessage = () => {
      const licenseKeyElement = document.querySelector(
        'div[style*="pointer-events: none"][style*="z-index: 100000"]'
      );
      if (licenseKeyElement) {
        licenseKeyElement.style.display = "none";
      }
    };

    hideLicenseKeyMessage();

    return () => {
      const licenseKeyElement = document.querySelector(
        'div[style*="pointer-events: none"][style*="z-index: 100000"]'
      );
      if (licenseKeyElement) {
        licenseKeyElement.style.display = "block";
      }
    };
  }, []);

  const toggleDatePicker = () => {
    if (loading) return;
    setIsOpen(!isOpen);
  };

  const handleShortcutSelect = (dates) => {
    setSelectedDates(dates);
  };

  const handleApply = () => {
    if (selectedDates[0] && selectedDates[1]) {
      setStartDateValue(dayjs(selectedDates[0]));
      setEndDateValue(dayjs(selectedDates[1]));
      setIsRefereshData((prev) => !prev);
      setIsOpen(false);
    }
  };

  const formatSelectedDates = () => {
    if (selectedDates[0] && selectedDates[1]) {
      return `${dayjs(selectedDates[0]).format("DD MMM")} - ${dayjs(
        selectedDates[1]
      ).format("DD MMM")}`;
    }
    return "Date";
  };

  const handleReset = () => {
    const today = new Date();
    setSelectedDates([today, today]);
    setStartDateValue(today);
    setEndDateValue(today);
  };

  const handleCalendarChange = (dates) => {
    if (dates.length === 2) {
      setSelectedDates(dates);
    }
  };

  // Mobile dialog version
  const MobileDatePickerDialog = () => (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          margin: isMobile ? 0 : 16,
          width: isMobile ? "90%" : "100%",
          maxHeight: isMobile ? "100vh" : "80vh",
          borderRadius: isMobile ? 0 : 8,
        },
      }}
    >
      <DialogContent style={{ padding: "16px", overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <Calendar
            selectRange={true}
            onChange={handleCalendarChange}
            value={selectedDates}
            className="mobile-calendar"
          />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "8px",
              marginBottom: "16px",
            }}
          >
            {shortcutsItems?.map((shortcut, index) => (
              <Button
                key={index}
                onClick={() => handleShortcutSelect(shortcut.getValue())}
                variant="outlined"
                size="small"
                style={{
                  fontSize: "12px",
                  padding: "6px 4px",
                  minWidth: "auto",
                }}
              >
                {shortcut.label}
              </Button>
            ))}
          </div>
        </div>
      </DialogContent>
      <DialogActions
        style={{
          padding: "0 16px 16px 16px",
          justifyContent: "center",
          gap: "12px",
        }}
      >
        <Button
          onClick={handleReset}
          disabled={loading}
          variant="outlined"
          color="primary"
          style={{ minWidth: "80px" }}
        >
          RESET
        </Button>
        <Button
          onClick={handleApply}
          disabled={loading}
          variant="contained"
          color="primary"
          style={{ minWidth: "80px" }}
        >
          APPLY
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Desktop popover version
  const DesktopDatePicker = () => (
    <div
      style={{
        // position: "absolute",
        // top: "100%",
        // right: isMobile ? "0" : "30%",
        // zIndex: 1000,
        // backgroundColor: "white",
        // border: "1px solid #ccc",
        // borderRadius: "10px",
        // boxShadow: "0 2px 5px rgba(0, 0, 0, 0.15)",
        // padding: "15px 10px",
        // height: "auto",
        // width: isMobile ? "100vw" : "auto",
        // maxWidth: isMobile ? "none" : "320px",
        // left: isMobile ? "0" : "auto",
        position: "absolute",
        top: "100%",
        right: "30%",
        zIndex: 1000,
        backgroundColor: "white",
        border: "1px solid #ccc",
        borderRadius: "10px",
        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.15)",
        padding: "15px 10px",
        height: "auto",
      }}
      ref={calendarRef}
    >
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Calendar
          selectRange={true}
          onChange={handleCalendarChange}
          value={selectedDates}
        />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "8px",
          marginTop: "10px",
        }}
      >
        {shortcutsItems?.map((shortcut, index) => (
          <div style={{ display: "flex" }} key={index}>
            <button
              onClick={() => handleShortcutSelect(shortcut.getValue())}
              style={{
                backgroundColor: "#f0f0f0",
                border: "1px solid #ccc",
                borderRadius: "10px",
                padding: "4px",
                marginBottom: "8px",
                cursor: "pointer",
                width: "100%",
                fontSize: "12px",
                textAlign: "center",
                color: "black",
              }}
            >
              {shortcut.label}
            </button>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: "20px", justifyContent: "center" }}>
        <button
          style={{
            backgroundColor: "#88BCDF",
            border: "none",
            borderRadius: "4px",
            padding: "8px 14px",
            fontSize: "12px",
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.3s ease",
            marginBottom: "-8px",
            color: "black",
          }}
          onClick={handleReset}
          disabled={loading}
        >
          RESET
        </button>
        <button
          style={{
            backgroundColor: "#69C173",
            color: "black",
            border: "none",
            borderRadius: "4px",
            padding: "8px 14px",
            fontSize: "12px",
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.3s ease",
            marginBottom: "-8px",
          }}
          onClick={handleApply}
          disabled={loading}
        >
          APPLY
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ position: "relative" }}>
      <div style={{ marginRight: "10px", marginTop: "5px" }}>
        <TextField
          label="Date"
          value={formatSelectedDates()}
          onClick={toggleDatePicker}
          disabled={loading}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={toggleDatePicker} size="small">
                  <CalendarMonthIcon />
                </IconButton>
              </InputAdornment>
            ),
            style: {
              fontSize: "14px",
              cursor: "pointer",
              transition: "border-color 0.3s, box-shadow 0.3s",
            },
            readOnly: true,
          }}
          variant="standard"
          size={isMobile ? "small" : "medium"}
        />
      </div>

      {isOpen && (
        <>{isMobile ? <MobileDatePickerDialog /> : <DesktopDatePicker />}</>
      )}

      {/* Style to hide the license key message and make calendar responsive */}
      <style>{`
        /* General styling for the date tiles in the current month */
        .react-calendar__month-view__days__day:not(.react-calendar__month-view__days__day--neighboringMonth) {
          color: black !important;
        }

        /* Style for weekends (Saturdays and Sundays) */
        .react-calendar__month-view__days__day--weekend {
          color: red !important;
        }

        .react-calendar__tile--range,
        .react-calendar__tile--rangeStart,
        .react-calendar__tile--rangeEnd {
          background: #44B150 !important;
          color: #fff !important;
          border-radius: 50% !important;
        }
        
        .react-calendar {
          border: none !important;
       
        }

        /* Navigation styling - set month navigation text to black */
        .react-calendar__navigation button {
          color: black !important;
        
        }

      `}</style>
    </div>
  );
};

export default DateRangePickerWithFilter;
